import React from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import {
  MarketingEmailMultiSelect,
  MARKETING_EMAIL_LANGUAGE_OPTIONS,
  MARKETING_EMAIL_PROFILE_STATUS_OPTIONS,
} from './marketing-email-audience-filters.jsx';

function MarketingEmailCampaignEditorPageView({
  mode,
  campaignRow,
  form,
  countryOptions = [],
  preview,
  isLoading,
  isSaving,
  isPreviewing,
  isSending,
  error,
  success,
  formErrors,
  canSend,
  onFormChange,
  saveCampaign,
  loadPreview,
  sendCampaign,
}) {
  const navigate = useNavigate();
  const isBusy = isSaving || isPreviewing || isSending;
  const title = mode === 'create' ? 'Create email campaign' : 'Edit email campaign';

  return (
    <div>
      <FloatingAlerts
        info={isBusy ? 'Processing email campaign request...' : ''}
        error={error}
        success={success}
      />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{title}</h2>
          <p>{campaignRow ? `${campaignRow.statusLabel} · ${campaignRow.recipientsLabel}` : 'Draft a campaign for eligible client profiles.'}</p>
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/marketing/emails/campaigns')}>
          Back to campaigns
        </button>
      </div>

      <LoadingContainer isLoading={isLoading} hasData>
        <div className="row g-3">
          <div className="col-xl-7">
            <div className="card card-body">
              <form onSubmit={saveCampaign}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Campaign name</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      value={form.name}
                      onChange={(event) => onFormChange('name', event.target.value)}
                      disabled={isBusy}
                      required
                    />
                    {formErrors.name ? <div className="invalid-feedback">{formErrors.name}</div> : null}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Subject</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.subject ? 'is-invalid' : ''}`}
                      value={form.subject}
                      onChange={(event) => onFormChange('subject', event.target.value)}
                      disabled={isBusy}
                      required
                    />
                    {formErrors.subject ? <div className="invalid-feedback">{formErrors.subject}</div> : null}
                  </div>
                  <MarketingEmailMultiSelect
                    name="countries"
                    label="Countries"
                    value={form.countries}
                    options={countryOptions}
                    onChange={onFormChange}
                    disabled={isBusy}
                  />
                  <MarketingEmailMultiSelect
                    name="profileStatuses"
                    label="Profile statuses"
                    value={form.profileStatuses}
                    options={MARKETING_EMAIL_PROFILE_STATUS_OPTIONS}
                    onChange={onFormChange}
                    disabled={isBusy}
                  />
                  <MarketingEmailMultiSelect
                    name="languages"
                    label="Languages"
                    value={form.languages}
                    options={MARKETING_EMAIL_LANGUAGE_OPTIONS}
                    onChange={onFormChange}
                    disabled={isBusy}
                  />
                  <div className="col-12">
                    <label className="form-label">HTML body</label>
                    <textarea
                      className={`form-control ${formErrors.htmlBody ? 'is-invalid' : ''}`}
                      rows="12"
                      value={form.htmlBody}
                      onChange={(event) => onFormChange('htmlBody', event.target.value)}
                      disabled={isBusy}
                      required
                    ></textarea>
                    {formErrors.htmlBody ? <div className="invalid-feedback">{formErrors.htmlBody}</div> : null}
                  </div>
                  <div className="col-12">
                    <label className="form-label">Text body</label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={form.textBody}
                      onChange={(event) => onFormChange('textBody', event.target.value)}
                      disabled={isBusy}
                    ></textarea>
                  </div>
                </div>

                <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
                  <button type="submit" className="btn btn-primary" disabled={isBusy || isLoading}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={loadPreview} disabled={isBusy || !campaignRow}>
                    {isPreviewing ? 'Rendering...' : 'Preview'}
                  </button>
                  <button type="button" className="btn btn-success" onClick={sendCampaign} disabled={isBusy || !canSend}>
                    {isSending ? 'Sending...' : 'Send campaign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="col-xl-5">
            <div className="card card-body h-100">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <h5 className="mb-0">Preview</h5>
                {preview?.unsubscribeUrl ? <span className="badge bg-info-subtle text-info">Unsubscribe token</span> : null}
              </div>
              {preview ? (
                <>
                  <div className="fw-semibold mb-2">{preview.subject}</div>
                  <div className="border rounded p-3 mb-3 bg-light" dangerouslySetInnerHTML={{ __html: preview.htmlBody }}></div>
                  <pre className="small text-muted mb-0 text-wrap">{preview.textBody || ''}</pre>
                </>
              ) : (
                <div className="text-muted">Save the campaign, then render a preview.</div>
              )}
            </div>
          </div>
        </div>
      </LoadingContainer>
    </div>
  );
}

export default MarketingEmailCampaignEditorPageView;
